import type { Identifier } from "ra-core";
import type { FinancePlan } from "./financeModel";
import type { ExpenseReview, ExpenseTotal } from "./expenseModel";
import { adjustedBalances, type BudgetAdjustment } from "./adjustmentModel";
export type AnalyticsFilter = {
  start: string | null;
  end: string | null;
  deal_id: Identifier | null;
};
export type AnalyticsExpense = ExpenseReview & {
  expense_snapshot: {
    spent_at: string;
    category: string;
    sales_id: Identifier;
    created_by_sales_id: Identifier;
  };
};
export type AnalyticsData = {
  projects: {
    id: Identifier;
    name: string;
    amount: number;
    archived_at?: string | null;
    created_at: string;
  }[];
  plans: FinancePlan[];
  totals: ExpenseTotal[];
  adjustments: BudgetAdjustment[];
  expenses: AnalyticsExpense[];
  tasks: {
    id: Identifier;
    deal_id?: Identifier | null;
    sales_id: Identifier;
    done_date: string;
    due_date?: string | null;
    status: string;
  }[];
  sales: { id: Identifier; first_name: string; last_name: string }[];
  companies: { id: Identifier; name: string }[];
};
export const inPeriod = (
  date: string,
  start: string | null,
  end: string | null,
) =>
  !!date &&
  (!start || date.slice(0, 10) >= start) &&
  (!end || date.slice(0, 10) <= end);
export const moscowDate = (date: string) =>
  new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Moscow",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(date));
export function buildAnalytics(data: AnalyticsData, filter: AnalyticsFilter) {
  const cents = (n: number) => Math.round(Number(n) * 100);
  const projects = data.projects.map((project) => {
    const plan = data.plans.find(
      (p) => String(p.deal_id) === String(project.id),
    );
    const rows = plan
      ? adjustedBalances(plan, data.totals, data.adjustments)
      : [];
    const spent = rows.reduce((s, r) => s + cents(r.spent), 0) / 100;
    const budget =
      rows
        .filter((r) => r.key !== "reserve")
        .reduce((s, r) => s + cents(r.budget), 0) / 100;
    return {
      ...project,
      plan,
      rows,
      spent,
      budget,
      remainder: plan ? Number(plan.price) - spent : null,
    };
  });
  const adjustments = data.adjustments.filter((a) =>
    inPeriod(moscowDate(a.created_at), filter.start, filter.end),
  );
  // Reversals remain in the journal; signed totals cancel original operations.
  const adjustmentsNet =
    adjustments.reduce(
      (s, a) => s + (a.reverses_id != null ? -1 : 1) * cents(a.amount),
      0,
    ) / 100;
  const expenses = data.expenses.filter(
    (e) =>
      e.decision === "approved" &&
      inPeriod(e.expense_snapshot.spent_at, filter.start, filter.end),
  );
  const spent = expenses.reduce((s, e) => s + cents(e.amount), 0) / 100;
  const reasons = new Map<
    string,
    { reason: string; expense: number; adjustment: number; count: number }
  >();
  for (const e of expenses) {
    const r = reasons.get(e.reason) ?? {
      reason: e.reason,
      expense: 0,
      adjustment: 0,
      count: 0,
    };
    r.expense += cents(e.amount);
    r.count++;
    reasons.set(e.reason, r);
  }
  for (const a of adjustments) {
    const r = reasons.get(a.reason) ?? {
      reason: a.reason,
      expense: 0,
      adjustment: 0,
      count: 0,
    };
    r.adjustment += (a.reverses_id != null ? -1 : 1) * cents(a.amount);
    reasons.set(a.reason, r);
  }
  const parties = [
    ...data.sales.map((s) => ({
      id: "sales:" + s.id,
      name: [s.first_name, s.last_name].join(" "),
      sales_id: s.id,
      company_id: null,
    })),
    ...data.companies.map((c) => ({
      id: "company:" + c.id,
      name: c.name,
      sales_id: null,
      company_id: c.id,
    })),
  ]
    .map((p) => {
      const matches = (a: {
        sales_id?: Identifier | null;
        company_id?: Identifier | null;
      }) =>
        p.sales_id != null
          ? String(a.sales_id) === String(p.sales_id)
          : String(a.company_id) === String(p.company_id);
      const zones = data.plans.flatMap((plan) =>
        plan.zones
          .filter(matches)
          .map((z) => ({ deal_id: plan.deal_id, key: z.key })),
      );
      const costs =
        data.totals
          .filter((t) =>
            zones.some(
              (z) =>
                String(z.deal_id) === String(t.deal_id) && z.key === t.zone_key,
            ),
          )
          .reduce((s, t) => s + cents(t.approved_amount), 0) / 100;
      const changes = adjustments.filter(matches);
      const done =
        p.sales_id == null
          ? []
          : data.tasks.filter(
              (t) =>
                String(t.sales_id) === String(p.sales_id) &&
                t.status === "completed" &&
                inPeriod(moscowDate(t.done_date), filter.start, filter.end),
            );
      const withDeadline = done.filter((t) => t.due_date);
      const onTime = withDeadline.filter(
        (t) => new Date(t.done_date) <= new Date(t.due_date!),
      ).length;
      return {
        ...p,
        projects: new Set(zones.map((z) => String(z.deal_id))).size,
        costs,
        corrections:
          changes.reduce(
            (s, a) => s + (a.reverses_id != null ? -1 : 1) * cents(a.amount),
            0,
          ) / 100,
        completed: done.length,
        withDeadline: withDeadline.length,
        onTime,
      };
    })
    .filter(
      (p) =>
        p.projects ||
        p.completed ||
        adjustments.some((a) =>
          p.sales_id != null
            ? String(a.sales_id) === String(p.sales_id)
            : String(a.company_id) === String(p.company_id),
        ),
    );
  return {
    projects,
    adjustments,
    adjustmentsNet,
    expenses,
    spent,
    reasons: [...reasons.values()].map((r) => ({
      ...r,
      expense: r.expense / 100,
      adjustment: r.adjustment / 100,
    })),
    parties,
  };
}
